import React, { useState } from 'react';
import {
  Modal,
  Backdrop,
  Fade,
  Tabs,
  Tab,
  Snackbar,
  IconButton,
  CircularProgress,
  Box,
  Typography,
  TextField,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { globalConfig } from '../configuration/config';
import { useTheme } from '@mui/material/styles';
import mainIcon from '../images/task.jpg';
import { useTranslation } from "react-i18next";

const CreateByPhotoModal = ({ onCloseMenu }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [modelName, setModelName] = useState('');
  const [modelNameError, setModelNameError] = useState('');
  const { t } = useTranslation();

  const [singleFile, setSingleFile] = useState(null);
  const [multiFiles, setMultiFiles] = useState({
    front: null,
    back: null,
    left: null,
    right: null,
  });
  const [uploadMessage, setUploadMessage] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const [uploadMessageType, setUploadMessageType] = useState(null); // 'error' | 'success' | 'info' | null

  const [widthMm, setWidthMm] = useState('');
  const [heightMm, setHeightMm] = useState('');
  const [depthMm, setDepthMm] = useState('');
  const [widthError, setWidthError] = useState('');
  const [heightError, setHeightError] = useState('');
  const [depthError, setDepthError] = useState('');

  // Блокировка нецелых/отрицательных значений в числовых полях
  const blockNonNumericKeys = (event) => {
    const blockedKeys = ['e', 'E', '+', '-', '.', ',', ' '];
    if (blockedKeys.includes(event.key)) {
      event.preventDefault();
    }
  };

  const handleIntChange = (setter) => (event) => {
    const digitsOnly = event.target.value.replace(/[^0-9]/g, '');
    setter(digitsOnly);
  };

  const handlePasteNumericOnly = (event) => {
    const text = (event.clipboardData || window.clipboardData).getData('text');
    if (!/^\d+$/.test(text)) {
      event.preventDefault();
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    if (isUploadComplete) onCloseMenu();
    resetState();
  };

  const resetState = () => {
    setTab(0);
    setModelName('');
    setSingleFile(null);
    setMultiFiles({ front: null, back: null, left: null, right: null });
    setModelNameError('');
    setUploadProgress(0);
    setIsUploadComplete(false);
    setLoading(false);
    setUploadMessage(null);
    setUploadMessageType(null);
    setWidthMm('');
    setHeightMm('');
    setDepthMm('');
    setWidthError('');
    setHeightError('');
    setDepthError('');
  };

  const handleModelNameChange = (e) => {
    setModelName(e.target.value);
    setModelNameError('');
  };

  const validateImage = (file) => {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return t('createByPhoto.errors.allowedFormats');
    }
    if (file.size > 10 * 1024 * 1024) {
      return t('createByPhoto.errors.maxSize');
    }
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        if (img.width < 128 || img.height < 128) {
          reject(new Error(t('createByPhoto.errors.minResolution')));
        } else {
          resolve(null);
        }
      };
      img.onerror = () => reject(new Error(t('createByPhoto.errors.loadFailed')));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleSingleDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    try {
      const error = await validateImage(file);
      if (error) throw new Error(error);
      setSingleFile(file);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleMultiDrop = (key) => async (acceptedFiles) => {
    const file = acceptedFiles[0];
    try {
      const error = await validateImage(file);
      if (error) throw new Error(error);
      setMultiFiles((prev) => ({ ...prev, [key]: file }));
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const removeMultiFile = (key) => {
    setMultiFiles((prev) => ({ ...prev, [key]: null }));
  };

  const removeSingleFile = () => {
    setSingleFile(null);
  };

  const uploadFiles = async () => {
    setUploadMessage(null);
    setUploadMessageType(null);

    if (!modelName.trim()) {
      setModelNameError(t('createByPhoto.errors.nameRequiredShort'));
      setUploadMessage(<Typography>{t('createByPhoto.errors.nameRequiredShort')}</Typography>);
      setUploadMessageType('error');
      return;
    }

    if (tab === 0) {
      if (!singleFile) {
        setUploadMessage(<Typography>{t('createByPhoto.errors.uploadOne')}</Typography>);
        setUploadMessageType('error');
        return;
      }

      // Необязательная валидация размеров для одиночного фото
      const w = widthMm !== '' ? parseInt(widthMm, 10) : null;
      const h = heightMm !== '' ? parseInt(heightMm, 10) : null;
      const d = depthMm !== '' ? parseInt(depthMm, 10) : null;
      let hasError = false;
      if (w !== null && (Number.isNaN(w) || w <= 0)) {
        setWidthError('Значение должно быть > 0');
        hasError = true;
      } else {
        setWidthError('');
      }
      if (h !== null && (Number.isNaN(h) || h <= 0)) {
        setHeightError('Значение должно быть > 0');
        hasError = true;
      } else {
        setHeightError('');
      }
      if (d !== null && (Number.isNaN(d) || d <= 0)) {
        setDepthError('Значение должно быть > 0');
        hasError = true;
      } else {
        setDepthError('');
      }
      if (hasError) return;
    } else {
      const { front, back, left, right } = multiFiles;
      if (!front || [back, left, right].filter(Boolean).length < 1) {
        setUploadMessage(<Typography>{t('createByPhoto.errors.needTwoWithFront')}</Typography>);
        setUploadMessageType('error');
        return;
      }
      // Проверяем размеры только если введены; поля не обязательны
      const w = widthMm !== '' ? parseInt(widthMm, 10) : null;
      const h = heightMm !== '' ? parseInt(heightMm, 10) : null;
      const d = depthMm !== '' ? parseInt(depthMm, 10) : null;
      let hasError = false;
      if (w !== null && (Number.isNaN(w) || w <= 0)) {
        setWidthError('Значение должно быть > 0');
        hasError = true;
      } else {
        setWidthError('');
      }
      if (h !== null && (Number.isNaN(h) || h <= 0)) {
        setHeightError('Значение должно быть > 0');
        hasError = true;
      } else {
        setHeightError('');
      }
      if (d !== null && (Number.isNaN(d) || d <= 0)) {
        setDepthError('Значение должно быть > 0');
        hasError = true;
      } else {
        setDepthError('');
      }
      if (hasError) return;
    }

    const formData = new FormData();
    const company = JSON.parse(localStorage.getItem('company'));
    formData.append('IdCompany', company?.id || 1);
    formData.append('Name', modelName);

    if (tab === 0) {
      formData.append('FileFront', singleFile);
      // Размеры в мм (если указаны)
      if (widthMm !== '') formData.append('Width', String(parseInt(widthMm, 10)));
      if (heightMm !== '') formData.append('Height', String(parseInt(heightMm, 10)));
      if (depthMm !== '') formData.append('Depth', String(parseInt(depthMm, 10)));
    } else {
      const { front, back, left, right } = multiFiles;
      if (front) formData.append('FileFront', front);
      if (back) formData.append('FileBack', back);
      if (left) formData.append('FileLeft', left);
      if (right) formData.append('FileRight', right);
      // Размеры в мм (если указаны)
      if (widthMm !== '') formData.append('Width', String(parseInt(widthMm, 10)));
      if (heightMm !== '') formData.append('Height', String(parseInt(heightMm, 10)));
      if (depthMm !== '') formData.append('Depth', String(parseInt(depthMm, 10)));
    }

    const maxRetries = 3;
    let attempt = 0;
    setLoading(true);
    setUploadProgress(0);
    setIsUploadComplete(false);

    while (attempt < maxRetries) {
      try {
        await axios.post(`${globalConfig.config.common.api}Projects/CreateByPhotoNew`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
          onUploadProgress: (e) => {
              const progress = Math.round((e.loaded * 100) / e.total);
              setUploadProgress(progress);
          },
        });

        setSnackbar({ open: true, message: t('createByPhoto.snackbar.success'), severity: 'success' });
        setUploadMessage(
          <>
            {t('createByPhoto.messages.allFilesUploaded')}{' '}
            {t('createByPhoto.messages.chatWillBeCreatedWithImage')}{' '}
            <img
              src={mainIcon}
              alt="Conversion Icon"
              style={{ width: '50px', height: '50px', verticalAlign: 'sub', margin: '0 5px' }}
            />{' '}
            {t('createByPhoto.messages.creationProcessShown')}{' '}
            {t('createByPhoto.messages.resultInSameChat')}
          </>
        );
        setUploadMessageType('success');
        setIsUploadComplete(true);
        setModelNameError('');
        return;
      } catch (error) {
        attempt += 1;
        console.error(`Attempt ${attempt} failed:`, error);

        if (attempt >= maxRetries) {
          setUploadMessage(<Typography>{t('createByPhoto.errors.uploadFailedLong')}</Typography>);
          setUploadMessageType('error');
        } else {
          setUploadMessage(<Typography>{t('createByPhoto.messages.retryAttempt', { attempt })}</Typography>);
          setUploadMessageType('error');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const renderPreview = (file, onRemove) => (
    <Box sx={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
      <img
        src={URL.createObjectURL(file)}
        alt="preview"
        style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8 }}
      />
      <IconButton
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          backgroundColor: '#eee',
          border: '2px solid transparent',
          color: '#000',
          '&:hover': { backgroundColor: '#eee', borderColor: 'transparent', color: '#000' },
        }}>
        <DeleteIcon />
      </IconButton>
    </Box>
  );

  const { getRootProps: getSingleRootProps, getInputProps: getSingleInputProps } = useDropzone({
    onDrop: handleSingleDrop,
    maxFiles: 1,
    accept: { 'image/*': [] },
  });

  const dropzones = [
    { key: 'front', label: t('createByPhoto.dropzones.front') },
    { key: 'back', label: t('createByPhoto.dropzones.back') },
    { key: 'left', label: t('createByPhoto.dropzones.left') },
    { key: 'right', label: t('createByPhoto.dropzones.right') },
  ];

  const multiDropzones = dropzones.reduce((acc, { key }) => {
    acc[key] = useDropzone({ onDrop: handleMultiDrop(key), maxFiles: 1, accept: { 'image/*': [] } });
    return acc;
  }, {});

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}>
        <Fade in={open}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[5],
              padding: theme.spacing(2),
              borderRadius: theme.shape.borderRadius,
              width: '90vw',
              maxWidth: '600px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}>
            <IconButton
              onClick={handleClose}
              sx={{ position: 'absolute', top: 4, right: 4, color: theme.palette.grey[500], zIndex: 1 }}>
              <CloseIcon />
            </IconButton>

            <Box sx={{ flex: '1 1 auto', overflowY: 'auto', pr: 1, pb: 1 }}>
              <div className="infoList--title" style={{ marginBottom: '8px', fontSize: '16px' }}>{ t("menu.createFromPhotosAndDrawings") }</div>

              <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ minHeight: '40px' }}>
                <Tab label={t('createByPhoto.tabs.single')} />
                <Tab label={t('createByPhoto.tabs.multiple')} />
              </Tabs>

              <label className="input-title" style={{ marginTop: '8px', marginBottom: '4px', display: 'block' }}>{ t('createByPhoto.labels.name') }</label>
              <TextField
                value={modelName}
                onChange={handleModelNameChange}
                error={!!modelNameError}
                helperText={modelNameError}
                fullWidth
                required
                placeholder={t('createByPhoto.placeholders.name')}
              style={{ 
                  mt: 1,
                  marginBottom: '8px',
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  width: 'calc(100% - 20px)'
                }}
              />

              <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 400, marginBottom: '8px' }}>
                { t('createByPhoto.hints.imageRules') }
              </Typography>

              {tab === 0 && (
              <>
                <Box
                  {...getSingleRootProps()}
                  sx={{
                    border: '1px dashed grey',
                    backgroundColor: '#f5f5f5',
                    height: 100,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    marginBottom: '8px'
                  }}>
                  <input {...getSingleInputProps()} />
                  {singleFile ? (
                    renderPreview(singleFile, removeSingleFile)
                  ) : (
                    <Typography sx={{ position: 'absolute', textAlign: 'center', fontSize: '14px' }}>
                      { t('createByPhoto.placeholders.uploadImage') }
                    </Typography>
                  )}
                </Box>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 1,
                  alignItems: 'start'
                }}>
                  <TextField
                    label="Ширина, мм"
                    type="number"
                    value={widthMm}
                    onChange={handleIntChange(setWidthMm)}
                    error={!!widthError}
                    helperText={widthError}
                    inputProps={{ min: 1, step: 1, inputMode: 'numeric', pattern: '\\d*' }}
                    onKeyDown={blockNonNumericKeys}
                    onPaste={handlePasteNumericOnly}
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                  />
                  <TextField
                    label="Высота, мм"
                    type="number"
                    value={heightMm}
                    onChange={handleIntChange(setHeightMm)}
                    error={!!heightError}
                    helperText={heightError}
                    inputProps={{ min: 1, step: 1, inputMode: 'numeric', pattern: '\\d*' }}
                    onKeyDown={blockNonNumericKeys}
                    onPaste={handlePasteNumericOnly}
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                  />
                  <TextField
                    label="Глубина, мм"
                    type="number"
                    value={depthMm}
                    onChange={handleIntChange(setDepthMm)}
                    error={!!depthError}
                    helperText={depthError}
                    inputProps={{ min: 1, step: 1, inputMode: 'numeric', pattern: '\\d*' }}
                    onKeyDown={blockNonNumericKeys}
                    onPaste={handlePasteNumericOnly}
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                  />
                </Box>
              </>
            )}

              {tab === 1 && (
              <>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 1,
                  marginBottom: '8px'
                }}>
                  {dropzones.map(({ key, label }) => (
                    <Box
                      key={key}
                      {...multiDropzones[key].getRootProps()}
                      sx={{
                        border: '1px dashed grey',
                        backgroundColor: '#f5f5f5',
                        height: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        cursor: 'pointer',
                        overflow: 'hidden',
                      }}>
                      <input {...multiDropzones[key].getInputProps()} />
                      {multiFiles[key] ? (
                        renderPreview(multiFiles[key], () => removeMultiFile(key))
                      ) : (
                        <Typography sx={{ position: 'absolute', textAlign: 'center', fontSize: '12px' }}>
                          {label}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 1,
                  alignItems: 'start'
                }}>
                  <TextField
                    label="Ширина, мм"
                    type="number"
                    value={widthMm}
                    onChange={handleIntChange(setWidthMm)}
                    error={!!widthError}
                    helperText={widthError}
                    inputProps={{ min: 1, step: 1, inputMode: 'numeric', pattern: '\\d*' }}
                    onKeyDown={blockNonNumericKeys}
                    onPaste={handlePasteNumericOnly}
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                  />
                  <TextField
                    label="Высота, мм"
                    type="number"
                    value={heightMm}
                    onChange={handleIntChange(setHeightMm)}
                    error={!!heightError}
                    helperText={heightError}
                    inputProps={{ min: 1, step: 1, inputMode: 'numeric', pattern: '\\d*' }}
                    onKeyDown={blockNonNumericKeys}
                    onPaste={handlePasteNumericOnly}
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                  />
                  <TextField
                    label="Глубина, мм"
                    type="number"
                    value={depthMm}
                    onChange={handleIntChange(setDepthMm)}
                    error={!!depthError}
                    helperText={depthError}
                    inputProps={{ min: 1, step: 1, inputMode: 'numeric', pattern: '\\d*' }}
                    onKeyDown={blockNonNumericKeys}
                    onPaste={handlePasteNumericOnly}
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                  />
                </Box>
              </>
            )}

              {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                  <CircularProgress size={16} />
                  <Typography sx={{ ml: 1, fontSize: '14px' }}>{ t('createByPhoto.labels.progress', { percent: uploadProgress }) }</Typography>
                </Box>
              )}
              {uploadMessage && (
                <Box
                  sx={{
                    marginTop: '8px',
                    p: 1,
                    backgroundColor:
                      uploadMessageType === 'error'
                        ? '#fcebea'
                        : uploadMessageType === 'success'
                        ? '#e6f4ea'
                        : '#f0f0f0',
                    borderRadius: 2,
                    fontSize: 12,
                    color:
                      uploadMessageType === 'error'
                        ? '#cc0000'
                        : uploadMessageType === 'success'
                        ? '#2e7d32'
                        : '#333',
                  }}>
                  {uploadMessage}
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                padding: '8px 16px',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                background: '#FFF',
                borderTop: '1px solid #eee',
                height: '60px',
                flexShrink: 0
              }}>
              <button
                onClick={isUploadComplete ? handleClose : uploadFiles}
                disabled={loading}
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  background: '#E0E0E0',
                  color: '#232323',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  padding: '8px 16px',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}>
                {isUploadComplete ? t('createByPhoto.buttons.ok') : t('createByPhoto.buttons.create')}
              </button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      <div className="bm-item menu-item" onClick={handleOpen}>
        { t("menu.createFromPhotosAndDrawings") }
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        sx={{ zIndex: 2000 }}>
        <MuiAlert elevation={6} variant="filled" severity={snackbar.severity}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default CreateByPhotoModal;
